import { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Zap, Send, Copy, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WeeklyNewsletter() {
  const [brandName, setBrandName] = useState('qlia.io');
  const [websiteUrl, setWebsiteUrl] = useState('https://qlia.io');
  const [topicFocus, setTopicFocus] = useState('AI, Cybersecurity & Leadership');
  const [generating, setGenerating] = useState(false);
  const [newsletter, setNewsletter] = useState(null);
  const [history, setHistory] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/newsletter/history`);
      setHistory(data.newsletters || []);
    } catch { }
  };

  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    setGenerating(true); setError(null); setNewsletter(null);
    try {
      const { data } = await axios.post(`${API}/api/newsletter/generate`, {
        brand_name: brandName, website_url: websiteUrl, topic_focus: topicFocus,
      });
      setNewsletter(data.newsletter);
      loadHistory();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setGenerating(false); }
  };

  const send = async () => {
    if (!newsletter?.id || !testEmail) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/newsletter/send`, {
        newsletter_id: newsletter.id, test_email: testEmail,
      });
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  const copy = () => {
    if (newsletter?.newsletter?.html_body) {
      navigator.clipboard.writeText(newsletter.newsletter.html_body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--llmc-bg)' }}>
      <div className="max-w-3xl mx-auto p-6 flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f0c27f, #fc5c7d)' }}>
            <Mail size={20} className="text-white" />
          </div>
          <div>
            <h1 className="font-black text-white text-lg">Weekly Newsletter</h1>
            <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>AI-compiled weekly digest → sent via Resend</p>
          </div>
        </div>

        <div className="llmc-card p-5 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--llmc-text-muted)' }}>Brand Name</label>
              <input value={brandName} onChange={e => setBrandName(e.target.value)}
                className="llmc-input w-full px-3 py-2 text-sm" data-testid="newsletter-brand-input" />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--llmc-text-muted)' }}>Website URL</label>
              <input value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)}
                className="llmc-input w-full px-3 py-2 text-sm" data-testid="newsletter-url-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--llmc-text-muted)' }}>Topic Focus</label>
            <input value={topicFocus} onChange={e => setTopicFocus(e.target.value)}
              className="llmc-input w-full px-3 py-2 text-sm" data-testid="newsletter-topic-input" />
          </div>

          {error && <div className="px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(235,87,87,0.1)', border: '1px solid rgba(235,87,87,0.3)', color: '#eb5757' }}>{error}</div>}

          <button onClick={generate} disabled={generating} data-testid="generate-newsletter-btn"
            className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white text-sm llmc-btn-primary disabled:opacity-40">
            {generating
              ? <><div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} /> Generating...</>
              : <><Zap size={16} /> Generate Newsletter</>}
          </button>
        </div>

        {newsletter && (
          <div className="fade-in llmc-card p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-white">Newsletter Draft</h2>
              <button onClick={copy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--llmc-text-muted)' }}>
                {copied ? <CheckCircle size={12} style={{ color: '#3dffa0' }} /> : <Copy size={12} />} Copy HTML
              </button>
            </div>

            <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="text-xs font-bold text-white mb-1">{newsletter.newsletter?.subject}</div>
              <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{newsletter.newsletter?.preview_text}</div>
            </div>

            {newsletter.newsletter?.send_recommendation && (
              <div className="text-[11px] px-3 py-2 rounded-lg" style={{ background: 'rgba(240,194,127,0.08)', border: '1px solid rgba(240,194,127,0.2)', color: '#f0c27f' }}>
                Recommendation: {newsletter.newsletter.send_recommendation}
              </div>
            )}

            <div className="flex gap-2">
              <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                className="flex-1 llmc-input px-3 py-2 text-sm" placeholder="Send test to email..."
                data-testid="test-email-input" />
              <button onClick={send} disabled={sending || !testEmail} data-testid="send-test-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all"
                style={{ background: '#f0c27f22', border: '1px solid rgba(240,194,127,0.3)', color: '#f0c27f' }}>
                <Send size={13} /> {sending ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="llmc-card p-5">
            <h2 className="text-sm font-bold text-white mb-3">History</h2>
            <div className="flex flex-col gap-1.5">
              {history.slice(0, 5).map(n => (
                <div key={n.id} className="flex items-center gap-3 px-3 py-2 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span className="text-xs font-medium text-white flex-1">{n.newsletter?.subject || 'Newsletter'}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ background: n.status === 'sent' ? 'rgba(61,255,160,0.1)' : 'rgba(255,255,255,0.05)', color: n.status === 'sent' ? '#3dffa0' : 'rgba(255,255,255,0.3)' }}>
                    {n.status}
                  </span>
                  <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
